/**
 * Published Reports Page
 * Final disclosure documents
 */
import React, { useState } from 'react';
import { 
  FileBarChart, 
  Download, 
  Eye, 
  Calendar,
  CheckCircle2,
  ExternalLink,
  Search,
  Filter
} from 'lucide-react';

const PUBLISHED_REPORTS = [
  {
    id: 1,
    name: 'FY2023 Annual Sustainability Report',
    framework: 'CSRD',
    publishedDate: '2024-06-28',
    format: 'PDF',
    pages: 145,
    downloads: 1250,
    status: 'published',
  },
  {
    id: 2,
    name: 'FY2023 TCFD Climate Report',
    framework: 'TCFD',
    publishedDate: '2024-04-10',
    format: 'PDF',
    pages: 42,
    downloads: 890,
    status: 'published',
  },
  {
    id: 3,
    name: '2024 CDP Climate Response',
    framework: 'CDP',
    publishedDate: '2024-07-29',
    format: 'Online',
    pages: null,
    downloads: null,
    status: 'submitted',
  },
  {
    id: 4,
    name: 'FY2023 GRI Standards Report',
    framework: 'GRI',
    publishedDate: '2024-06-15',
    format: 'PDF',
    pages: 98,
    downloads: 675,
    status: 'published',
  },
];

const STATUS_CONFIG = {
  published: { label: 'Published', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  submitted: { label: 'Submitted', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  draft: { label: 'Draft', bg: 'bg-amber-500/20', text: 'text-amber-400' },
};

export default function PublishedReportsPage() {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredReports = PUBLISHED_REPORTS.filter(report =>
    report.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalDownloads = PUBLISHED_REPORTS.reduce((sum, r) => sum + (r.downloads || 0), 0);

  return (
    <div className="space-y-6" data-testid="published-reports-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Published Reports</h1>
        <p className="text-sm text-[#6B6B6B] mt-1">
          Access and download your published disclosure documents
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <FileBarChart size={18} className="text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{PUBLISHED_REPORTS.length}</div>
              <div className="text-xs text-[#6B6B6B]">Total Reports</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <CheckCircle2 size={18} className="text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">
                {PUBLISHED_REPORTS.filter(r => r.status === 'published').length}
              </div>
              <div className="text-xs text-[#6B6B6B]">Published</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Download size={18} className="text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{totalDownloads.toLocaleString()}</div>
              <div className="text-xs text-[#6B6B6B]">Total Downloads</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Calendar size={18} className="text-amber-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">2024</div>
              <div className="text-xs text-[#6B6B6B]">Latest Year</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
        <input
          type="text"
          placeholder="Search reports..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none"
          data-testid="reports-search-input"
        />
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredReports.map(report => {
          const statusConfig = STATUS_CONFIG[report.status];
          
          return (
            <div 
              key={report.id} 
              className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5 hover:border-emerald-500/30 transition-all"
              data-testid={`report-card-${report.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-[#1F1F1F] text-[#A0A0A0] rounded">
                      {report.framework}
                    </span>
                    <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${statusConfig.bg} ${statusConfig.text}`}>
                      {statusConfig.label}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-white">{report.name}</h3>
                </div>
                <div className="p-2 rounded-lg bg-[#1F1F1F]">
                  <FileBarChart size={20} className="text-[#6B6B6B]" />
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-[#6B6B6B] mb-4">
                <span className="flex items-center gap-1">
                  <Calendar size={10} />
                  {new Date(report.publishedDate).toLocaleDateString()}
                </span>
                <span>{report.format}</span>
                {report.pages && <span>{report.pages} pages</span>}
                {report.downloads && <span>{report.downloads.toLocaleString()} downloads</span>}
              </div>

              <div className="flex items-center gap-2">
                {report.format === 'PDF' ? (
                  <>
                    <button className="flex-1 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1">
                      <Download size={12} /> Download
                    </button>
                    <button className="flex-1 py-2 bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#A0A0A0] text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1">
                      <Eye size={12} /> Preview
                    </button>
                  </>
                ) : (
                  <button className="flex-1 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-400 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1">
                    <ExternalLink size={12} /> View Online
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filteredReports.length === 0 && (
        <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl">
          <FileBarChart size={48} className="mx-auto text-[#2F2F2F] mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No reports found</h3>
          <p className="text-sm text-[#6B6B6B]">Try adjusting your search criteria</p>
        </div>
      )}
    </div>
  );
}
